<template>
  <div class="dashboard-container">
    <!-- HEADER SECTION -->
    <b-card class="mb-3 dashboard-header" no-body>
      <b-card-body class="d-flex justify-content-between align-items-center py-2">
        <div>
          <div class="d-flex align-items-center">
            <div class="header-icon-wrapper mr-2">
              <b-icon-people class="header-icon" />
            </div>
            <div>
              <h5 class="mb-0">SALESPERSON PERFORMANCE</h5>
              <small class="header-subtitle">Track performance metrics and commission analytics</small>
            </div>
          </div>
        </div>
        <div class="d-flex align-items-center">
          <b-badge variant="light" class="mr-2 date-badge">
            <b-icon-calendar class="mr-1" />{{ formattedDateRange }}
          </b-badge>
          <b-badge variant="primary" pill class="animated-badge">
            {{ filteredItems.length }} {{ filteredItems.length === 1 ? 'Agent' : 'Agents' }}
          </b-badge>
        </div>
      </b-card-body>
    </b-card>

    <!-- FILTERS CARD -->
    <b-card class="mb-3 filters-card" no-body>
      <b-card-body class="py-2">
        <div class="row align-items-end">
          <div class="col-lg-8">
            <div class="d-flex flex-wrap align-items-end" style="gap: 10px;">
              <div style="min-width: 250px;">
                <label class="small text-muted mb-1">
                  <b-icon-calendar class="mr-1" size="sm" />DATE RANGE
                </label>
                <date-picker
                  v-model="date_range"
                  format="MM-DD-YYYY"
                  valueType="format"
                  class="custom-datepicker"
                  placeholder="Select Date Range"
                  range
                  :class="{ 'datepicker-focused': dateRangeFocused }"
                  @focus="dateRangeFocused = true"
                  @blur="dateRangeFocused = false"
                ></date-picker>
              </div>

              <div class="d-flex flex-wrap" style="gap: 5px;">
                <b-button
                  v-for="btn in quickDateButtons"
                  :key="btn.id"
                  size="sm"
                  :variant="picked_date_range === btn.id ? 'danger' : 'outline-danger'"
                  @click="set_dates(btn.id)"
                  class="quick-date-btn py-1 px-2"
                >
                  <b-icon v-if="picked_date_range === btn.id" :icon="btn.icon" class="mr-1" size="sm" />
                  {{ btn.label }}
                </b-button>
              </div>
            </div>
          </div>
          
          <div class="col-lg-4 mt-2 mt-lg-0">
            <div class="d-flex justify-content-end">
              <b-button
                variant="success"
                @click="fetch_init"
                class="search-btn"
                :disabled="items_being_loaded"
              >
                <b-icon-search class="mr-1" /> 
                <span v-if="!items_being_loaded">SEARCH</span>
                <span v-else>
                  <b-icon-arrow-repeat spin class="mr-1" />LOADING...
                </span>
              </b-button>
            </div>
          </div>
        </div>

        <!-- ADVANCED FILTERS -->
        <div class="mt-2">
          <b-button 
            variant="link" 
            size="sm" 
            @click="showAdvancedFilters = !showAdvancedFilters"
            class="p-0 text-decoration-none"
          >
            <b-icon :icon="showAdvancedFilters ? 'chevron-up' : 'chevron-down'" class="mr-1" size="sm" />
            {{ showAdvancedFilters ? 'Hide' : 'Show' }} Filters
          </b-button>
          
          <b-collapse v-model="showAdvancedFilters">
            <div class="mt-2 pt-2 border-top">
              <div class="row">
                <div class="col-md-4 mb-2">
                  <label class="small text-muted mb-1">
                    <b-icon-building class="mr-1" size="sm" />LOCATION
                  </label>
                  <b-form-select
                    :options="filteredLocations"
                    v-model="picked_location"
                    size="sm"
                    class="filter-select"
                    @change="onFilterChange"
                  >
                    <option :value="null">All Locations</option>
                  </b-form-select>
                </div>

                <div class="col-md-4 mb-2">
                  <label class="small text-muted mb-1">
                    <b-icon-person class="mr-1" size="sm" />SALESPERSON
                  </label>
                  <b-form-select
                    :options="filteredUsers"
                    v-model="picked_user"
                    size="sm"
                    class="filter-select"
                    @change="onFilterChange"
                  >
                    <option :value="null">All Salespersons</option>
                  </b-form-select>
                </div>

                <div class="col-md-4 mb-2">
                  <label class="small text-muted mb-1">
                    <b-icon-tag class="mr-1" size="sm" />SALES TYPE
                  </label>
                  <b-form-select
                    size="sm"
                    :options="sales_types"
                    v-model="sales_type"
                    class="filter-select"
                    @change="onFilterChange"
                  ></b-form-select>
                </div>
              </div>
            </div>
          </b-collapse>
        </div>

        <!-- ACTIVE FILTERS DISPLAY -->
        <div class="mt-2 pt-2 border-top" v-if="selected_salesperson">
          <div class="d-flex flex-wrap align-items-center" style="gap: 10px;">
            <span class="small text-muted">Active Filter:</span>
            <b-badge variant="primary" pill class="active-filter-badge">
              <b-icon-person class="mr-1" size="xs" />{{ selected_salesperson }}
              <b-icon-x class="ml-1" size="xs" @click="clearSalespersonFilter" style="cursor: pointer;" />
            </b-badge>
            <span class="small text-muted ml-2">Showing data for selected salesperson only</span>
          </div>
        </div>
      </b-card-body>
    </b-card>

    <!-- LOADING STATE -->
    <div v-if="items_being_loaded" class="text-center py-4">
      <b-spinner variant="primary" type="grow" label="Loading..."></b-spinner>
      <p class="mt-2 text-muted small">Fetching sales data...</p>
    </div>

    <!-- MAIN CONTENT -->
    <template v-else-if="filteredItems.length > 0">
      <!-- COMPACT KPI CARDS -->
      <div class="row mb-2">
        <div class="col-xl-3 col-lg-6 col-md-6 mb-2">
          <div class="compact-kpi-card">
            <div class="compact-kpi-icon bg-primary">
              <b-icon-cash-stack />
            </div>
            <div class="compact-kpi-info">
              <span class="compact-kpi-label">Gross Sales</span>
              <span class="compact-kpi-value">{{ formatCurrency(totalGrossSales) }}</span>
              <small class="compact-kpi-sub">{{ totalNSales }} transactions</small>
            </div>
          </div>
        </div>

        <div class="col-xl-3 col-lg-6 col-md-6 mb-2">
          <div class="compact-kpi-card">
            <div class="compact-kpi-icon bg-success">
              <b-icon-wallet />
            </div>
            <div class="compact-kpi-info">
              <span class="compact-kpi-label">Net Sales</span>
              <span class="compact-kpi-value">{{ formatCurrency(totalNetSales) }}</span>
              <small class="compact-kpi-sub text-danger">Short: {{ formatCurrency(totalShortAmount) }}</small>
            </div>
          </div>
        </div>

        <div class="col-xl-3 col-lg-6 col-md-6 mb-2">
          <div class="compact-kpi-card">
            <div class="compact-kpi-icon bg-warning">
              <b-icon-calculator />
            </div>
            <div class="compact-kpi-info">
              <span class="compact-kpi-label">Avg Sale</span>
              <span class="compact-kpi-value">{{ formatCurrency(avgSaleValueOverall) }}</span>
              <small class="compact-kpi-sub">per transaction</small>
            </div>
          </div>
        </div>

        <div class="col-xl-3 col-lg-6 col-md-6 mb-2">
          <div class="compact-kpi-card">
            <div class="compact-kpi-icon bg-info">
              <b-icon-percent />
            </div>
            <div class="compact-kpi-info">
              <span class="compact-kpi-label">Collection Rate</span>
              <span class="compact-kpi-value">{{ collectionRate }}%</span>
              <small class="compact-kpi-sub">{{ formatCurrency(totalShortAmount) }} short</small>
            </div>
          </div>
        </div>
      </div>

      <!-- COMPACT STATS ROW -->
      <div class="row mb-2">
        <div class="col-3 mb-2">
          <div class="compact-stat-card">
            <div class="compact-stat-icon bg-primary-light">
              <b-icon-receipt />
            </div>
            <div class="compact-stat-info">
              <span class="compact-stat-value">{{ totalDeliveries }}</span>
              <span class="compact-stat-label">Deliveries</span>
            </div>
          </div>
        </div>
        <div class="col-3 mb-2">
          <div class="compact-stat-card">
            <div class="compact-stat-icon bg-success-light">
              <b-icon-tools />
            </div>
            <div class="compact-stat-info">
              <span class="compact-stat-value">{{ totalSetups }}</span>
              <span class="compact-stat-label">Setups</span>
            </div>
          </div>
        </div>
        <div class="col-3 mb-2">
          <div class="compact-stat-card">
            <div class="compact-stat-icon bg-warning-light">
              <b-icon-shield-check />
            </div>
            <div class="compact-stat-info">
              <span class="compact-stat-value">{{ totalWarranties }}</span>
              <span class="compact-stat-label">Warranties</span>
            </div>
          </div>
        </div>
        <div class="col-3 mb-2">
          <div class="compact-stat-card">
            <div class="compact-stat-icon bg-danger-light">
              <b-icon-arrow-return-left />
            </div>
            <div class="compact-stat-info">
              <span class="compact-stat-value">{{ totalRefunds }}</span>
              <span class="compact-stat-label">Refunds</span>
            </div>
          </div>
        </div>
      </div>

      <!-- DATA TABLE SECTION -->
      <b-card no-body class="mt-2 data-table-card">
        <b-card-header class="bg-light d-flex justify-content-between align-items-center py-1">
          <div class="d-flex align-items-center">
            <b-icon-table class="mr-2" size="sm" />
            <h6 class="mb-0">Salesperson Performance Details</h6>
            <b-badge variant="secondary" class="ml-2">{{ filteredItems.length }} {{ filteredItems.length === 1 ? 'agent' : 'agents' }}</b-badge>
            <b-badge variant="light" class="ml-2" v-if="!selected_salesperson">
              <b-icon-mouse2 size="sm" class="mr-1" />Click row to filter
            </b-badge>
            <b-badge variant="info" class="ml-2" v-else>
              <b-icon-check-circle size="sm" class="mr-1" />Filtered by salesperson
            </b-badge>
          </div>
          <div>
            <b-button v-if="packet.userDetails.userrole == 'admin'" size="sm" variant="outline-secondary" @click="exportToCSV" class="mr-1">
              <b-icon-download class="mr-1" size="sm" /> Export
            </b-button>
            <b-button size="sm" variant="outline-primary" @click="fetch_init">
              <b-icon-arrow-repeat class="mr-1" size="sm" /> Refresh
            </b-button>
          </div>
        </b-card-header>
        <b-card-body class="p-0">
          <b-overlay :show="items_being_loaded" spinner-variant="primary" spinner-type="grow" spinner-small rounded="sm">
            <div v-if="filteredItems.length > 0" class="table-wrapper-sm">
              <table class="compact-table">
                <thead>
                  <tr>
                    <th @click="sortby('username')">AGENT</th>
                    <th @click="sortby('location_id')">LOCATION</th>
                    <th class="text-right" @click="sortby('n_sales')"># SALES</th>
                    <th class="text-right" @click="sortby('total_dollar_amount')">GROSS ($)</th>
                    <th class="text-right" @click="sortby('total_short_amount')">SHORT ($)</th>
                    <th class="text-right" @click="sortby('net_sales_amount')">NET ($)</th>
                    <th class="text-right" @click="sortby('average_sale_amount')">AVG ($)</th>
                    <th class="text-right">DELIVERIES</th>
                    <th class="text-right">SETUPS</th>
                    <th class="text-right">WARRANTIES</th>
                    <th class="text-right">REFUNDS</th>
                    <th v-if="packet.userDetails.userrole == 'admin'" class="text-right">COMM ($)</th>
                    <th class="text-right">SPIFF ($)</th>
                    <th class="text-right">WRNTY COMM ($)</th>
                    <th v-if="packet.userDetails.userrole == 'admin'" class="text-right">TOTAL COMM ($)</th>
                  </tr>
                </thead>
                <tbody>
                  <!-- TOTALS ROW -->
                  <tr class="totals-row">
                    <td colspan="2" class="text-right"><strong>TOTALS:</strong></td>
                    <td class="text-right"><strong>{{ totalNSales }}</strong></td>
                    <td class="text-right"><strong>{{ formatCurrency(totalGrossSales) }}</strong></td>
                    <td class="text-right text-danger"><strong>{{ formatCurrency(totalShortAmount) }}</strong></td>
                    <td class="text-right text-success"><strong>{{ formatCurrency(totalNetSales) }}</strong></td>
                    <td class="text-right"><strong>{{ formatCurrency(avgSaleValueOverall) }}</strong></td>
                    <td class="text-right"><strong>{{ totalDeliveries }}</strong></td>
                    <td class="text-right"><strong>{{ totalSetups }}</strong></td>
                    <td class="text-right"><strong>{{ totalWarranties }}</strong></td>
                    <td class="text-right"><strong>{{ totalRefunds }}</strong></td>
                    <td v-if="packet.userDetails.userrole == 'admin'" class="text-right text-primary"><strong>{{ formatCurrency(totalCommissionAll) }}</strong></td>
                    <td class="text-right"><strong>{{ formatCurrency(totalSpiffAll) }}</strong></td>
                    <td class="text-right"><strong>{{ formatCurrency(totalWarrantyCommAll) }}</strong></td>
                    <td v-if="packet.userDetails.userrole == 'admin'" class="text-right text-primary"><strong>{{ formatCurrency(totalCommissionAll + totalSpiffAll + totalWarrantyCommAll + totalDeliveriesCommAll + totalSetupsCommAll) }}</strong></td>
                  </tr>
                  
                  <!-- DATA ROWS -->
                  <tr v-for="(item, idx) in paginatedItems" :key="idx" 
                      :class="{ 
                        'top-performer-row': item.net_sales_amount > 10000, 
                        'low-performer-row': item.net_sales_amount < 1000 && item.net_sales_amount > 0,
                        'highlight-row': selected_salesperson === item.username
                      }"
                      @click="selectSalespersonFromTable(item.username)">
                    <td>
                      <div class="agent-info">
                        <div class="agent-avatar" :style="{ backgroundColor: getAvatarColor(item.username) }">
                          {{ getInitials(item.username) }}
                        </div>
                        <strong>{{ item.username }}</strong>
                      </div>
                    </td>
                    <td>
                      <b-badge variant="light" class="location-badge">
                        {{ packet.locationsdict[item.location_id] || item.location_id }}
                      </b-badge>
                    </td>
                    <td class="text-right">{{ item.n_sales || 0 }}</td>
                    <td class="text-right">{{ formatCurrency(item.total_dollar_amount || 0) }}</td>
                    <td class="text-right text-danger font-weight-bold">{{ formatCurrency(item.total_short_amount || 0) }}</td>
                    <td class="text-right text-success font-weight-bold">{{ formatCurrency(item.net_sales_amount || 0) }}</td>
                    <td class="text-right">{{ formatCurrency(item.average_sale_amount || 0) }}</td>
                    <td class="text-right">{{ item.total_deliveries_sold || 0 }}</td>
                    <td class="text-right">{{ item.total_setups_sold || 0 }}</td>
                    <td class="text-right">{{ item.total_warranties_sold || 0 }}</td>
                    <td class="text-right text-danger">{{ item.total_refunds || 0 }}</td>
                    <td v-if="packet.userDetails.userrole == 'admin'" class="text-right">{{ formatCurrency(item.commission || 0) }}</td>
                    <td class="text-right">{{ formatCurrency(item.spiff_commission || 0) }}</td>
                    <td class="text-right">{{ formatCurrency(item.total_warranties_commission || 0) }}</td>
                    <td v-if="packet.userDetails.userrole == 'admin'" class="text-right font-weight-bold text-primary">
                      {{ formatCurrency((item.total_warranties_commission || 0) + (item.spiff_commission || 0) + (item.commission || 0) + (item.total_deliveries_commission || 0) + (item.total_setups_commission || 0)) }}
                    </td>
                  </tr>
                </tbody>
              </table>
              
              <!-- Pagination -->
              <div class="pagination-wrapper d-flex justify-content-between align-items-center p-2 bg-light">
                <small class="text-muted">
                  Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, filteredItems.length) }} of {{ filteredItems.length }} agents
                </small>
                <b-pagination
                  v-model="currentPage"
                  :total-rows="filteredItems.length"
                  :per-page="pageSize"
                  size="sm"
                  class="mb-0"
                  align="center"
                  @change="handlePageChange"
                ></b-pagination>
                <b-form-select v-model="pageSize" size="sm" style="width: 65px;" @change="currentPage = 1">
                  <option :value="10">10</option>
                  <option :value="25">25</option>
                  <option :value="50">50</option>
                  <option :value="100">100</option>
                </b-form-select>
              </div>
            </div>
            <div v-else class="p-4 text-center">
              <b-alert show variant="light" class="mb-0">
                <b-icon-inbox class="mr-2" />
                No sales data available. Select a date range and click SEARCH.
              </b-alert>
            </div>
          </b-overlay>
        </b-card-body>
      </b-card>
    </template>

    <!-- NO DATA STATE -->
    <div v-else-if="!items_being_loaded && filteredItems.length === 0 && items.length > 0" class="text-center py-4">
      <b-alert show variant="info" class="mb-0">
        <b-icon-filter-circle class="mr-2" />
        No data matches the selected filters. Try adjusting your filters.
      </b-alert>
    </div>

    <div v-else-if="!items_being_loaded && items.length === 0" class="text-center py-4">
      <b-alert show variant="light" class="mb-0">
        <b-icon-search class="mr-2" />
        Select a date range and click SEARCH to view salesperson performance
      </b-alert>
    </div>
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import moment from 'moment';
import 'moment/locale/en-gb';
import Requestor from '@/utilities/Requestor';

import {
  BIconPeople,
  BIconCalendar,
  BIconSearch,
  BIconCashStack,
  BIconWallet,
  BIconCalculator,
  BIconTable,
  BIconArrowRepeat,
  BIconDownload,
  BIconBuilding,
  BIconPerson,
  BIconTag,
  BIconCartCheck,
  BIconExclamationCircle,
  BIconGraphUp,
  BIconReceipt,
  BIconTools,
  BIconShieldCheck,
  BIconArrowReturnLeft,
  BIconInbox,
  BIconX,
  BIconMouse2,
  BIconCheckCircle,
  BIconPercent,
  BIconFilterCircle,
  BIconChevronUp,
  BIconChevronDown
} from 'bootstrap-vue';

export default {
  name: "ReportsSalespersons",
  components: {
    DatePicker,
    BIconPeople,
    BIconCalendar,
    BIconSearch,
    BIconCashStack,
    BIconWallet,
    BIconCalculator,
    BIconTable,
    BIconArrowRepeat,
    BIconDownload,
    BIconBuilding,
    BIconPerson,
    BIconTag,
    BIconCartCheck,
    BIconExclamationCircle,
    BIconGraphUp,
    BIconReceipt,
    BIconTools,
    BIconShieldCheck,
    BIconArrowReturnLeft,
    BIconInbox,
    BIconX,
    BIconMouse2,
    BIconCheckCircle,
    BIconPercent,
    BIconFilterCircle,
    BIconChevronUp,
    BIconChevronDown
  },
  data: function() {
    return {
      packet: {},
      items: [],
      picked_location: null,
      picked_user: null,
      selected_salesperson: null,
      items_being_loaded: false,
      date_range: [],
      picked_date_range: null,
      sales_types: [
        { value: null, text: "All Sales" },
        { value: "delivered", text: "Delivered Only" },
        { value: "written", text: "Written Only" },
      ],
      sales_type: null,
      sortKey: null,
      sortAsc: false,
      users: [],
      usersdict: {},
      dateRangeFocused: false,
      currentPage: 1,
      pageSize: 25,
      showAdvancedFilters: false,
      quickDateButtons: [
        { id: 'today', label: 'Today', icon: 'calendar-day' },
        { id: 'yesterday', label: 'Yesterday', icon: 'calendar-day' },
        { id: 'this_week', label: 'This Week', icon: 'calendar-week' },
        { id: 'last_week', label: 'Last Week', icon: 'calendar-week' },
        { id: 'this_month', label: 'This Month', icon: 'calendar-month' },
        { id: 'last_month', label: 'Last Month', icon: 'calendar-month' }
      ],
    }
  },
  computed: {
    formattedDateRange() {
      if (this.date_range && this.date_range.length === 2) {
        return `${this.date_range[0]} - ${this.date_range[1]}`;
      }
      return 'Select dates';
    },
    
    filteredItems() {
      let filtered = this.items.filter(item => this.allowed(item));
      
      if (this.selected_salesperson) {
        filtered = filtered.filter(item => item.username === this.selected_salesperson);
      }
      
      if (this.sortKey) {
        filtered = filtered.sort((a, b) => {
          let aVal = a[this.sortKey] || 0;
          let bVal = b[this.sortKey] || 0;
          return this.sortAsc ? aVal - bVal : bVal - aVal;
        });
      }
      
      return filtered;
    },
    
    paginatedItems() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.filteredItems.slice(start, end);
    },
    
    filteredUsers() {
      if (this.items.length === 0) return this.users;
      const usersWithData = new Set(this.items.map(item => item.username));
      return this.users.filter(user =>
        user.value === null || usersWithData.has(user.value)
      );
    },
    
    filteredLocations() {
      if (this.items.length === 0 || !this.packet.locations) return this.packet.locations || [];
      const locationsWithData = new Set(this.items.map(item => item.location_id));
      return this.packet.locations.filter(location =>
        location.value === null || locationsWithData.has(location.value)
      );
    },
    
    totalGrossSales() {
      return this.filteredItems.reduce((sum, item) => sum + (parseFloat(item.total_dollar_amount) || 0), 0);
    },
    
    totalShortAmount() {
      return this.filteredItems.reduce((sum, item) => sum + (parseFloat(item.total_short_amount) || 0), 0);
    },
    
    totalNetSales() {
      return this.filteredItems.reduce((sum, item) => sum + (parseFloat(item.net_sales_amount) || 0), 0);
    },
    
    totalNSales() {
      return this.filteredItems.reduce((sum, item) => sum + (parseInt(item.n_sales) || 0), 0);
    },
    
    totalDeliveries() {
      return this.filteredItems.reduce((sum, item) => sum + (parseInt(item.total_deliveries_sold) || 0), 0);
    },
    
    totalSetups() {
      return this.filteredItems.reduce((sum, item) => sum + (parseInt(item.total_setups_sold) || 0), 0);
    },
    
    totalWarranties() {
      return this.filteredItems.reduce((sum, item) => sum + (parseInt(item.total_warranties_sold) || 0), 0);
    },
    
    totalRefunds() {
      return this.filteredItems.reduce((sum, item) => sum + (parseInt(item.total_refunds) || 0), 0);
    },
    
    totalCommissionAll() {
      return this.filteredItems.reduce((sum, item) => sum + (parseFloat(item.commission) || 0), 0);
    },
    
    totalSpiffAll() {
      return this.filteredItems.reduce((sum, item) => sum + (parseFloat(item.spiff_commission) || 0), 0);
    },
    
    totalWarrantyCommAll() {
      return this.filteredItems.reduce((sum, item) => sum + (parseFloat(item.total_warranties_commission) || 0), 0);
    },
    
    totalDeliveriesCommAll() {
      return this.filteredItems.reduce((sum, item) => sum + (parseFloat(item.total_deliveries_commission) || 0), 0);
    },
    
    totalSetupsCommAll() {
      return this.filteredItems.reduce((sum, item) => sum + (parseFloat(item.total_setups_commission) || 0), 0);
    },
    
    avgSaleValue() {
      if (this.filteredItems.length === 0) return 0;
      return this.totalNetSales / this.filteredItems.length;
    },
    
    avgSaleValueOverall() {
      const totalSales = this.totalNSales;
      if (totalSales === 0) return 0;
      return this.totalNetSales / totalSales;
    },
    
    totalTransactions() {
      return this.totalNSales;
    },
    
    collectionRate() {
      const total = this.totalGrossSales;
      if (total === 0) return 0;
      const shortRate = (this.totalShortAmount / total) * 100;
      return Math.max(0, 100 - shortRate).toFixed(1);
    },
    
    commissionRate() {
      const total = this.totalNetSales;
      if (total === 0) return 0;
      return ((this.totalCommissionAll / total) * 100).toFixed(1);
    }
  },
  watch: {
    filteredItems() {
      this.currentPage = 1;
    },
    picked_user() {
      this.currentPage = 1;
      this.selected_salesperson = null;
    },
    picked_location() {
      this.currentPage = 1;
    }
  },
  methods: {
    formatCurrency(value) {
      if (value === undefined || value === null) return '0.00';
      return parseFloat(value).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    
    getInitials(name) {
      if (!name) return '?';
      return name.charAt(0).toUpperCase();
    },
    
    getAvatarColor(name) {
      const colors = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#8b5cf6', '#ec4899'];
      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = ((hash << 5) - hash) + name.charCodeAt(i);
        hash |= 0;
      }
      return colors[Math.abs(hash) % colors.length];
    },
    
    allowed: function(item) {
      if (this.picked_user == null && this.picked_location == null) {
        return true;
      }
      if (this.picked_user != null && this.picked_location == null) {
        return item.username == this.picked_user;
      }
      if (this.picked_user == null && this.picked_location != null) {
        return item.location_id == this.picked_location;
      }
      if (this.picked_user != null && this.picked_location != null) {
        return item.username == this.picked_user && item.location_id == this.picked_location;
      }
      return true;
    },
    
    selectSalespersonFromTable(salesperson) {
      if (this.selected_salesperson === salesperson) {
        this.selected_salesperson = null;
      } else {
        this.selected_salesperson = salesperson;
        if (this.picked_user === salesperson) {
          this.picked_user = null;
        }
      }
      this.currentPage = 1;
    },
    
    clearSalespersonFilter() {
      this.selected_salesperson = null;
      this.currentPage = 1;
    },
    
    onFilterChange() {
      this.selected_salesperson = null;
      this.currentPage = 1;
    },
    
    set_dates: function(value) {
      let start = null;
      let end = null;
      
      const formats = {
        today: () => {
          start = moment().format("MM-DD-YYYY");
          end = moment().format("MM-DD-YYYY");
        },
        yesterday: () => {
          start = moment().add(-1, 'days').format("MM-DD-YYYY");
          end = moment().add(-1, 'days').format("MM-DD-YYYY");
        },
        this_week: () => {
          start = moment().startOf('week').format("MM-DD-YYYY");
          end = moment().endOf('week').format("MM-DD-YYYY");
        },
        last_week: () => {
          start = moment().subtract(1, 'weeks').startOf('week').format("MM-DD-YYYY");
          end = moment().subtract(1, 'weeks').endOf('week').format("MM-DD-YYYY");
        },
        this_month: () => {
          start = moment().startOf('month').format("MM-DD-YYYY");
          end = moment().endOf('month').format("MM-DD-YYYY");
        },
        last_month: () => {
          start = moment().subtract(1, 'months').startOf('month').format("MM-DD-YYYY");
          end = moment().subtract(1, 'months').endOf('month').format("MM-DD-YYYY");
        }
      };
      
      if (formats[value]) {
        formats[value]();
        if (start && end) {
          this.date_range = [start, end];
          this.picked_date_range = value;
          this.fetch_init();
        }
      }
    },
    
    fetch_init: function() {
      let dates = this.date_range;
      let that = this;
      let data = {
        dates: dates,
        username: this.packet.userDetails.username,
        sales_type: this.sales_type,
        location_id: this.picked_location,
        salesperson: this.picked_user
      };
      let path = "/api/reports/reports_v1/";
      
      let s = function(response) {
        if (response.data.result) {
          that.items = response.data.result;
          that.selected_salesperson = null;
          that.$bvToast.toast(
            `Loaded ${that.items.length} salesperson records`,
            { title: "Success", autoHideDelay: 1500, variant: "success" }
          );
        } else {
          that.$bvToast.toast(
            response.data.error || "No data returned",
            { title: "Info", autoHideDelay: 1500, variant: "info" }
          );
        }
        that.items_being_loaded = false;
      };

      let f = function(error) {
        console.log(error);
        that.$bvToast.toast(
          "Error occurred. Please try again.",
          { title: "Error", autoHideDelay: 1500, variant: "danger" }
        );
        that.items_being_loaded = false;
      };
      
      this.items_being_loaded = true;
      Requestor.postSyncP(path, data, this, s, f);
    },
    
    sortby: function(key) {
      if (this.sortKey === key) {
        this.sortAsc = !this.sortAsc;
      } else {
        this.sortKey = key;
        this.sortAsc = true;
      }
    },
    
    handlePageChange(page) {
      this.currentPage = page;
    },
    
    exportToCSV() {
      const headers = [
        'Agent', 'Location', '# Sales', 'Gross ($)', 'Short Amount ($)', 
        'Net ($)', '# Short Pmt', 'Avg Sale ($)', 'Total Sales Tax ($)',
        '# Delvrs', 'Total Delvrs ($)', '# Setups', 'Total Setups ($)',
        '# Warrants', 'Total Warrants ($)', '# Refunds', 'Total Refunds ($)',
        'Comm ($)', 'Spiff ($)', 'Warranty Comm ($)', 'Total Comm ($)'
      ];
      
      const rows = this.filteredItems.map(item => [
        item.username,
        this.packet.locationsdict[item.location_id] || item.location_id,
        item.n_sales || 0,
        (parseFloat(item.total_dollar_amount) || 0).toFixed(2),
        (parseFloat(item.total_short_amount) || 0).toFixed(2),
        (parseFloat(item.net_sales_amount) || 0).toFixed(2),
        item.n_short_payments || 0,
        (parseFloat(item.average_sale_amount) || 0).toFixed(2),
        (parseFloat(item.total_sales_tax) || 0).toFixed(2),
        item.total_deliveries_sold || 0,
        (parseFloat(item.total_deliveries_value) || 0).toFixed(2),
        item.total_setups_sold || 0,
        (parseFloat(item.total_setups_value) || 0).toFixed(2),
        item.total_warranties_sold || 0,
        (parseFloat(item.total_warranties_value) || 0).toFixed(2),
        item.total_refunds || 0,
        (parseFloat(item.total_refunds_value) || 0).toFixed(2),
        (parseFloat(item.commission) || 0).toFixed(2),
        (parseFloat(item.spiff_commission) || 0).toFixed(2),
        (parseFloat(item.total_warranties_commission) || 0).toFixed(2),
        ((parseFloat(item.total_warranties_commission) || 0) + 
         (parseFloat(item.spiff_commission) || 0) + 
         (parseFloat(item.commission) || 0) + 
         (parseFloat(item.total_deliveries_commission) || 0) + 
         (parseFloat(item.total_setups_commission) || 0)).toFixed(2)
      ]);
      
      const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `salesperson_report_${moment().format('YYYY-MM-DD')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      
      this.$bvToast.toast('Export completed!', { title: 'Success', autoHideDelay: 1500, variant: 'success' });
    },
    
    fetch_users_list: function() {
      let data = { userrole: this.packet.userDetails.userrole, user_id: this.packet.userDetails.id };
      let that = this;
      let path = "/api/user/get_my_users_list/";
      
      let s = function(response) {
        if (response.data.result) {
          that.users = response.data.result;
          that.users.splice(0, 0, { value: null, text: "All Salespersons" });
          for (var i = 0; i < that.users.length; i++) {
            that.usersdict[that.users[i].value] = true;
          }
        } else {
          that.$bvToast.toast(
            response.data.error || "Error loading users",
            { title: "Error", autoHideDelay: 1500, variant: "warning" }
          );
        }
      };
      
      let f = function(error) {
        console.log(error);
        that.$bvToast.toast(
          "Error occurred loading users.",
          { title: "Error", autoHideDelay: 1500, variant: "danger" }
        );
      };
      
      Requestor.getAsyncP(path, data, this, s, f);
    },
  },
  created: function() {
    this.packet = JSON.parse(localStorage.packet);
    this.packet.userDetails = JSON.parse(localStorage.userDetails);
    
    const start = moment().startOf('month').format("MM-DD-YYYY");
    const end = moment().endOf('month').format("MM-DD-YYYY");
    this.date_range = [start, end];
    this.picked_date_range = 'this_month';

    if (this.packet.userDetails.userrole == "admin" || this.packet.userDetails.userrole == "locationadmin") {
      this.fetch_users_list();
    }
    
    this.fetch_init();
  }
}
</script>

<style scoped>
.dashboard-container {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  min-height: 100vh;
  padding: 12px;
}

/* Header Styles */
.dashboard-header {
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  color: white;
  border: none;
  border-radius: 14px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.header-icon-wrapper {
  width: 36px;
  height: 36px;
  background: rgba(255,255,255,0.15);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-icon { font-size: 18px; animation: pulse 2s infinite; }
@keyframes pulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.05); } }

.dashboard-header h5 {
  color: white;
  font-weight: 600;
}

.header-subtitle {
  color: rgba(255,255,255,0.8);
}

.date-badge {
  background-color: rgba(255,255,255,0.15);
  color: white;
  border: none;
  padding: 2px 10px;
  border-radius: 14px;
  font-size: 11px;
}

.animated-badge {
  animation: fadeIn 0.5s ease-out;
}

/* Filters Card */
.filters-card {
  border: none;
  border-radius: 14px;
  box-shadow: 0 1px 5px rgba(0,0,0,0.05);
  background: white;
}

.custom-datepicker {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 4px 8px;
  font-size: 12px;
  width: 100%;
}

.datepicker-focused {
  border-color: #6366f1;
  box-shadow: 0 0 0 2px rgba(99,102,241,0.1);
}

.quick-date-btn {
  border-radius: 14px;
  font-size: 11px;
  padding: 3px 8px;
}

.filter-select {
  border-radius: 7px;
  background: #f8fafc;
  border-color: #e2e8f0;
  font-size: 12px;
}

.search-btn {
  border-radius: 8px;
  padding: 4px 20px;
  font-weight: 600;
  font-size: 12px;
}

.active-filter-badge {
  cursor: default;
  background: #e2e8f0;
  color: #1e293b;
  padding: 6px 12px;
}

/* Compact KPI Cards */
.compact-kpi-card {
  background: white;
  border-radius: 10px;
  padding: 8px 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  transition: all 0.2s;
}

.compact-kpi-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 3px 10px rgba(0,0,0,0.1);
}

.compact-kpi-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
}

.bg-primary { background: linear-gradient(135deg, #6366f1, #8b5cf6); }
.bg-success { background: linear-gradient(135deg, #10b981, #34d399); }
.bg-danger { background: linear-gradient(135deg, #ef4444, #f87171); }
.bg-warning { background: linear-gradient(135deg, #f59e0b, #fbbf24); }
.bg-info { background: linear-gradient(135deg, #06b6d4, #22d3ee); }
.bg-purple { background: linear-gradient(135deg, #8b5cf6, #a78bfa); }

.compact-kpi-info {
  display: flex;
  flex-direction: column;
}

.compact-kpi-label {
  font-size: 10px;
  color: #64748b;
  display: block;
}

.compact-kpi-value {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  display: block;
  line-height: 1.2;
}

.compact-kpi-sub {
  font-size: 9px;
  color: #94a3b8;
}

/* Compact Stat Cards */
.compact-stat-card {
  background: white;
  border-radius: 10px;
  padding: 10px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  transition: all 0.2s;
}

.compact-stat-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 3px 10px rgba(0,0,0,0.1);
}

.compact-stat-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

.bg-primary-light {
  background: rgba(99, 102, 241, 0.1);
  color: #6366f1;
}

.bg-success-light {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.bg-warning-light {
  background: rgba(245, 158, 11, 0.1);
  color: #f59e0b;
}

.bg-danger-light {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.compact-stat-info {
  display: flex;
  flex-direction: column;
}

.compact-stat-value {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.2;
}

.compact-stat-label {
  font-size: 10px;
  color: #94a3b8;
}

/* Data Table */
.data-table-card {
  border: none;
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  overflow: hidden;
}

.table-wrapper-sm {
  overflow-x: auto;
}

.compact-table {
  width: 100%;
  font-size: 11px;
  border-collapse: collapse;
}

.compact-table th,
.compact-table td {
  padding: 6px 8px;
  border: 1px solid #dee2e6;
}

.compact-table th {
  background: #c3cdd3;
  font-weight: 600;
  position: sticky;
  top: 0;
  white-space: nowrap;
  cursor: pointer;
}

.compact-table td {
  white-space: nowrap;
}

.compact-table tbody tr {
  cursor: pointer;
  transition: background 0.2s;
}

.compact-table tbody tr:hover {
  background: #e0e7ff !important;
}

.compact-table tbody tr.highlight-row {
  background: #dbeafe !important;
  border-left: 3px solid #6366f1;
}

.totals-row {
  background: #fef3c7;
  font-weight: 600;
}

.top-performer-row {
  background: linear-gradient(90deg, rgba(16, 185, 129, 0.05), transparent);
}

.low-performer-row {
  background: linear-gradient(90deg, rgba(239, 68, 68, 0.05), transparent);
}

/* Agent Info */
.agent-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.agent-avatar {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  color: white;
  font-size: 12px;
}

.location-badge {
  font-size: 10px;
  padding: 2px 8px;
  border-radius: 10px;
}

.pagination-wrapper {
  border-top: 1px solid #e2e8f0;
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.compact-kpi-card, .compact-stat-card, .data-table-card, .filters-card {
  animation: fadeIn 0.5s ease-out;
}

/* Text colors */
.text-danger {
  color: #dc3545 !important;
}

.text-success {
  color: #28a745 !important;
}

.font-weight-bold {
  font-weight: bold !important;
}

.border-top {
  border-top: 1px solid #e2e8f0;
}

.text-right {
  text-align: right !important;
}

.text-primary {
  color: #6366f1 !important;
}

/* Responsive */
@media (max-width: 768px) {
  .dashboard-container { padding: 8px; }
  .compact-kpi-value { font-size: 13px; }
  .compact-table th, .compact-table td { padding: 4px 6px; font-size: 9px; }
  .compact-kpi-icon { width: 32px; height: 32px; font-size: 14px; }
  .compact-stat-icon { width: 28px; height: 28px; font-size: 14px; }
  .dashboard-header h5 { font-size: 14px; }
  .header-subtitle { font-size: 10px; }
  .agent-avatar { width: 22px; height: 22px; font-size: 10px; }
}
</style>


// WEBPACK FOOTER //
// src/components/Reports/ReportsReportsV1.vue